import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { FileSignature, Plus, Trash2, Download, UserCheck } from 'lucide-react';
import { isoDate } from '../../utils/time';
import { useConfirm } from '../../components/ConfirmDialog';
import {
  PageHead, Register, FigureStrip, Figure, DetailDrawer, Field, FormRow, AcStatus, Category, money, fmtDate,
} from './design';

// Quotes: what a prospective participant would receive and what it costs, before they sign up.
//
// Brevity's intake starts here: a lead becomes a quote with a per-service budget, and an accepted
// quote converts into the client, their funding and a schedule template. Rostery does the same:
// Convert creates the participant, their Support at Home funding plan with the quoted services as
// approved services, this quarter's budget and a weekly service template to roster from.

const INP = 'w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';
const DAYS = [['mon', 'Mon'], ['tue', 'Tue'], ['wed', 'Wed'], ['thu', 'Thu'], ['fri', 'Fri'], ['sat', 'Sat'], ['sun', 'Sun']];
const MEANS_LABEL = { full_pensioner: 'Full pensioner', part_pensioner: 'Part pensioner', self_funded: 'Self-funded retiree', not_disclosed: 'Not yet assessed' };
const STATUS = { draft: 'Draft', sent: 'Sent', accepted: 'Accepted', declined: 'Declined', converted: 'Converted' };
const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const emptyLine = () => ({ serviceTypeId: '', days: [], hours: 1, startTime: '09:00' });

// The same arithmetic as the server's estimateQuote, so the totals update while typing.
function estimate(form, setup) {
  if (!setup) return null;
  const byId = Object.fromEntries((setup.services || []).map((s) => [s.id, s]));
  const means = form.means_category || 'not_disclosed';
  const start = form.start_date || isoDate();
  let lines = (form.lines || []).map((l) => {
    const svc = byId[l.serviceTypeId];
    if (!svc) return null;
    const visits = (l.days || []).length;
    const hours = Math.max(0, Math.round((Number(l.hours) || 0) * 4) / 4);
    const perVisit = r2(svc.hourly ? svc.rate * hours : svc.rate);
    const quarterly = r2(perVisit * visits * 13);
    const personalFree = svc.personalCare && start >= '2026-10-01';
    const pct = svc.category === 'clinical' || personalFree ? 0 : Number(setup.matrix?.[svc.category]?.[means] || 0);
    const contribution = r2(quarterly * pct / 100);
    return { ...svc, category: personalFree ? 'clinical' : svc.category, visits, hours, perVisit, quarterly, contribution, pct };
  });
  // `rows` stays index-aligned with the form's lines (null for a line with no service chosen).
  const rows = lines;
  lines = rows.filter(Boolean);
  // Interim funding: the quote is worked out at the interim share of the classification.
  const full = Number(form.quarterly_budget) || 0;
  const interimPct = form.is_interim ? (Number(form.interim_pct) || Number(setup?.interimPct) || 60) : null;
  const budget = interimPct ? r2(full * interimPct / 100) : full;
  const keep = 1 - (Number(setup?.cmSharePct ?? 10) / 100);
  const pool = r2(budget * keep);
  const services = r2(lines.reduce((n, l) => n + l.quarterly, 0));
  const contribution = r2(lines.reduce((n, l) => n + l.contribution, 0));
  return { rows, lines, budget, careManagement: r2(budget - pool), pool, services, remaining: r2(pool - services), contribution, government: r2(services - contribution),
    interimPct, fullRemaining: interimPct ? r2(r2(full * keep) - services) : null };
}

function saveBlob(blob, name) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = name; a.click();
  window.URL.revokeObjectURL(url);
}

export default function AgedCareQuotes({ onGoTo }) {
  const confirm = useConfirm();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [setup, setSetup] = useState(null);
  const [editing, setEditing] = useState(null); // null | 'new' | quote
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { const r = await api.get('/aged-care/quotes'); setQuotes(r.data || []); }
    catch (e) { setError(e.response?.data?.message || 'Could not load quotes'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/aged-care/quotes/setup').then((r) => setSetup(r.data)).catch(() => setSetup({ services: [], rates: {}, matrix: {} })); }, []);

  const open = (q) => {
    setEditing(q || 'new');
    const valid = new Date(); valid.setDate(valid.getDate() + 30);
    setForm(q ? {
      first_name: q.first_name || '', last_name: q.last_name || '', date_of_birth: q.date_of_birth ? String(q.date_of_birth).slice(0, 10) : '',
      email: q.email || '', phone: q.phone || '', mac_id: q.mac_id || '', level: q.level || '', means_category: q.means_category || '',
      quarterly_budget: q.quarterly_budget != null ? String(Number(q.quarterly_budget)) : '', start_date: q.start_date ? String(q.start_date).slice(0, 10) : '',
      is_interim: !!q.is_interim, interim_pct: q.interim_pct != null ? String(Number(q.interim_pct)) : '',
      valid_until: q.valid_until ? String(q.valid_until).slice(0, 10) : '', notes: q.notes || '',
      lines: (q.lines || []).map((l) => ({ ...emptyLine(), ...l })),
    } : {
      first_name: '', last_name: '', date_of_birth: '', email: '', phone: '', mac_id: '', level: '', means_category: '',
      quarterly_budget: '', is_interim: false, interim_pct: '', start_date: isoDate(), valid_until: valid.toISOString().slice(0, 10), notes: '', lines: [emptyLine()],
    });
  };
  const close = useCallback(() => { setEditing(null); setForm(null); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setLevel = (v) => setForm((f) => ({ ...f, level: v, quarterly_budget: setup?.rates?.[v] != null && (!f.quarterly_budget || Number(f.quarterly_budget) === Number(setup?.rates?.[f.level])) ? String(setup.rates[v]) : f.quarterly_budget }));
  const setLine = (i, patch) => setForm((f) => ({ ...f, lines: f.lines.map((l, j) => (j === i ? { ...l, ...patch } : l)) }));
  const est = useMemo(() => (form ? estimate(form, setup) : null), [form, setup]);
  const locked = editing && editing !== 'new' && editing.status === 'converted';

  const save = async () => {
    if (!form.first_name.trim() || !form.last_name.trim()) { toast.error('Enter the first and last name'); return; }
    setSaving(true);
    try {
      const body = { ...form, level: form.level ? Number(form.level) : null, quarterly_budget: Number(form.quarterly_budget) || 0,
        interim_pct: form.is_interim && form.interim_pct ? Number(form.interim_pct) : null };
      if (editing === 'new') await api.post('/aged-care/quotes', body);
      else await api.patch(`/aged-care/quotes/${editing.id}`, body);
      toast.success('Quote saved'); close(); load();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not save the quote'); }
    finally { setSaving(false); }
  };
  const pdf = async (q) => {
    try {
      const r = await api.get(`/aged-care/quotes/${q.id}/pdf`, { responseType: 'blob' });
      saveBlob(r.data, `quote-${(q.name || 'participant').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.pdf`);
    } catch { toast.error('Could not build the quote PDF'); }
  };
  const setStatus = async (q, status) => {
    try { await api.patch(`/aged-care/quotes/${q.id}`, { status }); toast.success(`Marked ${({ draft: 'as a draft', sent: 'as sent', accepted: 'accepted', declined: 'declined' })[status] || status}`); load(); } catch (e) { toast.error(e.response?.data?.message || 'Could not update the quote'); }
  };
  const convert = async (q, allowDuplicate = false) => {
    const over = (q.estimate?.remaining || 0) < 0
      ? ` The quoted services are ${money(-q.estimate.remaining)} a quarter over the budget, so the visits that do not fit will be held until there is funding.`
      : '';
    const ok = allowDuplicate || await confirm(
      `${q.name} becomes a participant with a Support at Home funding plan (classification ${q.level || '?'}), this quarter's budget of ${money(q.quarterly_budget)}, and a weekly service template with the quoted visits.${over}`,
      { title: 'Convert this quote?', confirmLabel: 'Convert to participant' });
    if (!ok) return;
    try {
      const r = await api.post(`/aged-care/quotes/${q.id}/convert`, { allow_duplicate: allowDuplicate });
      toast.success(`${q.name} is now a participant${r.data.templateId ? ', with a service template to roster from' : ''}.`);
      close(); load();
    } catch (e) {
      if (e.response?.status === 409 && e.response?.data?.duplicate) {
        const again = await confirm(e.response.data.message, { title: 'Already a participant', confirmLabel: 'Create a second record' });
        if (again) convert(q, true);
        return;
      }
      toast.error(e.response?.data?.message || 'Could not convert the quote');
    }
  };
  const remove = async (q) => {
    const ok = await confirm('The quote is deleted. A converted quote is kept as the record of what was offered.', { title: `Delete the quote for ${q.name}?`, confirmLabel: 'Delete quote', danger: true });
    if (!ok) return;
    try { await api.delete(`/aged-care/quotes/${q.id}`); toast.success('Quote deleted'); load(); } catch (e) { toast.error(e.response?.data?.message || 'Could not delete the quote'); }
  };

  const open_ = quotes.filter((q) => ['draft', 'sent', 'accepted'].includes(q.status));
  const columns = [
    { key: 'name', header: 'Prospective participant', sortable: true, render: (q) => (
      <div className="min-w-0">
        <div className="font-medium text-slate-900">{q.name || 'Unnamed'}</div>
        <div className="text-[12px] text-slate-500">{q.level ? `Classification ${q.level}` : 'Not yet classified'}{q.start_date ? `, from ${fmtDate(q.start_date)}` : ''}</div>
      </div>
    ) },
    { key: 'services', header: 'Services a quarter', align: 'right', sortable: true, sortValue: (q) => q.estimate?.services || 0, render: (q) => (
      <div>
        <div className="tabular-nums">{money(q.estimate?.services)}</div>
        <div className={clsx('text-[11px] tabular-nums', (q.estimate?.remaining || 0) < 0 ? 'text-red-700' : 'text-slate-500')}>
          {(q.estimate?.remaining || 0) < 0 ? `${money(-q.estimate.remaining)} over` : `${money(q.estimate?.remaining)} left`} of {money(q.estimate?.pool)}
        </div>
      </div>
    ) },
    { key: 'contribution', header: 'They pay a quarter', align: 'right', render: (q) => <span className="tabular-nums">{money(q.estimate?.contribution)}</span> },
    { key: 'status', header: 'Status', width: 120, render: (q) => <AcStatus status={q.status === 'converted' ? 'approved' : q.status === 'accepted' ? 'current' : q.status === 'declined' ? 'declined' : q.status === 'sent' ? 'submitted' : 'draft'} label={STATUS[q.status] || q.status} /> },
    { key: 'act', header: <span className="sr-only">Actions</span>, width: 230, align: 'right', render: (q) => (
      <div className="flex justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
        <button type="button" onClick={() => pdf(q)} className="btn-ghost btn-sm" title="Download the quote as a PDF"><Download size={13} aria-hidden="true" /> PDF</button>
        {q.status === 'converted'
          ? <button type="button" onClick={() => onGoTo?.('participants')} className="btn-ghost btn-sm">Open participant</button>
          : <button type="button" onClick={() => convert(q)} className="btn-secondary btn-sm"><UserCheck size={13} aria-hidden="true" /> Convert</button>}
      </div>
    ) },
  ];

  return (
    <div>
      <PageHead icon={FileSignature}
        title="Quotes"
        purpose="What a prospective participant would get and what it would cost them, before they sign up. Converting an accepted quote creates the participant, their funding plan, this quarter's budget and a weekly service template."
        actions={<button type="button" onClick={() => open(null)} className="btn-primary"><Plus size={15} aria-hidden="true" /> New quote</button>}
      />
      <FigureStrip>
        <Figure label="Open quotes" value={open_.length} />
        <Figure label="Accepted, ready to convert" value={quotes.filter((q) => q.status === 'accepted').length} />
        <Figure label="Converted" value={quotes.filter((q) => q.status === 'converted').length} />
        <Figure label="Quoted a quarter (open)" value={money(open_.reduce((n, q) => n + (q.estimate?.services || 0), 0))} />
      </FigureStrip>
      <Register
        title="Quotes" icon={FileSignature} count={quotes.length} countLabel={quotes.length === 1 ? 'quote' : 'quotes'}
        columns={columns} rows={quotes} rowKey="id" loading={loading} error={error} onRetry={load}
        onRowClick={(q) => open(q)}
        empty={{ icon: FileSignature, title: 'No quotes yet', hint: 'Quote a prospective participant their services, budget and contribution before they sign up.', action: <button type="button" onClick={() => open(null)} className="btn-primary btn-sm"><Plus size={14} /> New quote</button> }}
      />

      <DetailDrawer
        open={!!editing} onClose={close} width="xl" icon={FileSignature}
        title={editing === 'new' ? 'New quote' : form ? `Quote for ${[form.first_name, form.last_name].filter(Boolean).join(' ')}` : 'Quote'}
        subtitle={locked ? 'Converted to a participant. Kept as the record of what was offered.' : 'Prices are your Support at Home prices from the service list.'}
        footer={<>
          {editing && editing !== 'new' && !locked && <button type="button" onClick={() => remove(editing)} className="btn-ghost text-sm mr-auto hover:!text-red-700"><Trash2 size={14} /> Delete</button>}
          {editing && editing !== 'new' && !locked && (
            <select aria-label="Quote status" value={editing.status} onChange={(e) => { setStatus(editing, e.target.value); setEditing({ ...editing, status: e.target.value }); }} className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm">
              {['draft', 'sent', 'accepted', 'declined'].map((s) => <option key={s} value={s}>{STATUS[s]}</option>)}
            </select>
          )}
          <button type="button" onClick={close} className="btn-secondary text-sm">{locked ? 'Close' : 'Cancel'}</button>
          {!locked && <button type="button" onClick={save} disabled={saving} className="btn-primary text-sm">{saving ? 'Saving…' : 'Save quote'}</button>}
        </>}
      >
        {form && (
          <fieldset disabled={locked} className="space-y-5">
            <FormRow cols={2}>
              <Field label="First name">{(id) => <input id={id} className={INP} value={form.first_name} onChange={(e) => set('first_name', e.target.value)} />}</Field>
              <Field label="Last name">{(id) => <input id={id} className={INP} value={form.last_name} onChange={(e) => set('last_name', e.target.value)} />}</Field>
            </FormRow>
            <FormRow cols={3}>
              <Field label="Date of birth">{(id) => <input id={id} type="date" className={INP} value={form.date_of_birth} onChange={(e) => set('date_of_birth', e.target.value)} />}</Field>
              <Field label="Email">{(id) => <input id={id} type="email" className={INP} value={form.email} onChange={(e) => set('email', e.target.value)} />}</Field>
              <Field label="Phone">{(id) => <input id={id} className={INP} value={form.phone} onChange={(e) => set('phone', e.target.value)} />}</Field>
            </FormRow>
            <FormRow cols={3}>
              <Field label="Classification">{(id) => (
                <select id={id} className={INP} value={form.level} onChange={(e) => setLevel(e.target.value)}>
                  <option value="">Not yet assessed</option>
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => <option key={n} value={n}>Classification {n}</option>)}
                </select>
              )}</Field>
              <Field label="Quarterly budget" hint={form.level && setup?.rates?.[form.level] ? `Classification ${form.level}: ${money(setup.rates[form.level])} a quarter in Rates and fees` : undefined}>{(id) => (
                <input id={id} type="number" min="0" step="0.01" className={INP} value={form.quarterly_budget} onChange={(e) => set('quarterly_budget', e.target.value)} />
              )}</Field>
              <Field label="Contribution category">{(id) => (
                <select id={id} className={INP} value={form.means_category} onChange={(e) => set('means_category', e.target.value)}>
                  <option value="">Not yet assessed</option>
                  {Object.entries(MEANS_LABEL).filter(([k]) => k !== 'not_disclosed').map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              )}</Field>
            </FormRow>
            <div className="flex flex-wrap items-center gap-3 text-[13px]">
              <label className="flex items-center gap-2 text-slate-700">
                <input type="checkbox" checked={!!form.is_interim} onChange={(e) => set('is_interim', e.target.checked)} />
                Interim funding until full funding is released
              </label>
              {form.is_interim && (
                <label className="flex items-center gap-2 text-slate-600">
                  Share
                  <input type="number" min="1" max="100" step="1" aria-label="Interim share (%)" className="input-field !w-20 !py-1" value={form.interim_pct}
                    placeholder={String(setup?.interimPct ?? 60)} onChange={(e) => set('interim_pct', e.target.value)} />
                  %
                </label>
              )}
            </div>
            <FormRow cols={3}>
              <Field label="My Aged Care ID">{(id) => <input id={id} className={INP} value={form.mac_id} onChange={(e) => set('mac_id', e.target.value)} placeholder="AC12345678" />}</Field>
              <Field label="Proposed start">{(id) => <input id={id} type="date" className={INP} value={form.start_date} onChange={(e) => set('start_date', e.target.value)} />}</Field>
              <Field label="Valid until">{(id) => <input id={id} type="date" className={INP} value={form.valid_until} onChange={(e) => set('valid_until', e.target.value)} />}</Field>
            </FormRow>

            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-[14px] font-semibold text-slate-900">Services</h3>
                {!locked && <button type="button" onClick={() => set('lines', [...form.lines, emptyLine()])} className="btn-ghost btn-sm"><Plus size={13} /> Add a service</button>}
              </div>
              {!setup?.services?.length && (
                <p className="rounded-lg bg-amber-50 px-3 py-2 text-[13px] text-amber-900 mb-2">No service type is linked to a priced Support at Home item yet. Set prices in Aged Care, Service list.</p>
              )}
              <div className="space-y-2.5">
                {form.lines.map((l, i) => {
                  const line = est?.rows[i];
                  const svc = (setup?.services || []).find((s) => s.id === l.serviceTypeId);
                  return (
                    <div key={i} className="rounded-xl border border-slate-200 p-3 space-y-2.5">
                      <div className="flex gap-2 items-start">
                        <select aria-label="Service" className={clsx(INP, 'flex-1')} value={l.serviceTypeId} onChange={(e) => setLine(i, { serviceTypeId: e.target.value })}>
                          <option value="">Choose a service</option>
                          {(setup?.services || []).map((s) => <option key={s.id} value={s.id}>{s.name} ({money(s.rate)}{s.hourly ? ' an hour' : ` per ${s.unit}`})</option>)}
                        </select>
                        {!locked && <button type="button" onClick={() => set('lines', form.lines.filter((_, j) => j !== i))} className="btn-ghost btn-sm !px-2 hover:!text-red-700" aria-label="Remove service"><Trash2 size={14} /></button>}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                        <div className="flex gap-1" role="group" aria-label="Days">
                          {DAYS.map(([k, label]) => {
                            const on = (l.days || []).includes(k);
                            return (
                              <button key={k} type="button" aria-pressed={on}
                                onClick={() => setLine(i, { days: on ? l.days.filter((d) => d !== k) : [...(l.days || []), k] })}
                                className={clsx('rounded-md px-2 py-1 text-[12px] font-medium border', on ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-slate-600 border-slate-200')}>{label}</button>
                            );
                          })}
                        </div>
                        <label className="flex items-center gap-1.5 text-[13px] text-slate-600">Start
                          <input type="time" value={l.startTime || ''} onChange={(e) => setLine(i, { startTime: e.target.value })} className="border border-slate-200 rounded-lg px-2 py-1 text-sm" />
                        </label>
                        {(!svc || svc.hourly) && (
                          <label className="flex items-center gap-1.5 text-[13px] text-slate-600">Hours
                            <input type="number" min="0.25" step="0.25" value={l.hours} onChange={(e) => setLine(i, { hours: e.target.value })} className="w-20 border border-slate-200 rounded-lg px-2 py-1 text-sm tabular-nums" />
                          </label>
                        )}
                        {svc && <Category value={line?.category || svc.category} />}
                        {line && (
                          <span className="ml-auto text-[13px] tabular-nums text-slate-700">
                            {money(line.quarterly)} a quarter{line.contribution > 0 ? <span className="text-slate-500">, they pay {money(line.contribution)}</span> : <span className="text-slate-500">, nothing to pay</span>}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {est && (
              <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-[13px]">
                <div><div className="text-slate-500">For services</div><div className="font-display text-[18px] font-semibold tabular-nums">{money(est.pool)}</div><div className="text-[11px] text-slate-500">after {money(est.careManagement)} care management{est.interimPct ? `, at ${est.interimPct}% interim funding` : ''}</div></div>
                <div><div className="text-slate-500">Quoted a quarter</div><div className="font-display text-[18px] font-semibold tabular-nums">{money(est.services)}</div></div>
                <div><div className="text-slate-500">{est.remaining < 0 ? 'Over budget by' : 'Left over'}</div><div className={clsx('font-display text-[18px] font-semibold tabular-nums', est.remaining < 0 ? 'text-red-700' : 'text-emerald-700')}>{money(Math.abs(est.remaining))}</div></div>
                <div><div className="text-slate-500">They pay a quarter</div><div className="font-display text-[18px] font-semibold tabular-nums">{money(est.contribution)}</div><div className="text-[11px] text-slate-500">government {money(est.government)}</div></div>
                {est.interimPct ? (
                  <p className="col-span-2 sm:col-span-4 text-[12px] text-slate-600">
                    When full funding is released the budget steps up to {money(est.budget * 100 / est.interimPct)} a quarter: {est.fullRemaining >= 0 ? `${money(est.fullRemaining)} left over` : `${money(-est.fullRemaining)} over`} with these services. Review the care plan and services then.
                  </p>
                ) : null}
              </div>
            )}
            <Field label="Notes">{(id) => <textarea id={id} rows={3} className={INP} value={form.notes} onChange={(e) => set('notes', e.target.value)} />}</Field>
          </fieldset>
        )}
      </DetailDrawer>
    </div>
  );
}
